import {
  READER_WORKSPACE_SNAPSHOT_VERSION,
  type HornerProgressState,
  type ReaderWorkspaceSnapshot,
} from './types';

const DATABASE_NAME = 'kjvreader-ui';
const DATABASE_VERSION = 2;
const STORE_NAME = 'workspace';
const SNAPSHOT_KEY = 'current';
const HORNER_PROGRESS_KEY = 'horner-progress';

export interface ReaderWorkspaceStore {
  load(): Promise<ReaderWorkspaceSnapshot | null>;
  save(snapshot: ReaderWorkspaceSnapshot): Promise<void>;
}

export interface ReaderHornerProgressStore {
  loadHornerProgress(): Promise<HornerProgressState | null>;
  saveHornerProgress(progress: HornerProgressState): Promise<void>;
}

export class ReaderWorkspaceStorage implements ReaderWorkspaceStore, ReaderHornerProgressStore {
  async load(): Promise<ReaderWorkspaceSnapshot | null> {
    const db = await openWorkspaceDatabase();
    if (!db) return null;

    try {
      const snapshot = await requestToPromise<unknown>(
        db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(SNAPSHOT_KEY),
      );
      return isWorkspaceSnapshot(snapshot) ? snapshot : null;
    } finally {
      db.close();
    }
  }

  async save(snapshot: ReaderWorkspaceSnapshot): Promise<void> {
    const db = await openWorkspaceDatabase();
    if (!db) return;

    try {
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);
      await requestToPromise(store.put(snapshot, SNAPSHOT_KEY));
    } finally {
      db.close();
    }
  }

  async loadHornerProgress(): Promise<HornerProgressState | null> {
    const db = await openWorkspaceDatabase();
    if (!db) return null;

    try {
      const progress = await requestToPromise<unknown>(
        db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(HORNER_PROGRESS_KEY),
      );
      return isHornerProgressState(progress) ? progress : null;
    } finally {
      db.close();
    }
  }

  async saveHornerProgress(progress: HornerProgressState): Promise<void> {
    const db = await openWorkspaceDatabase();
    if (!db) return;

    try {
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);
      await requestToPromise(store.put(progress, HORNER_PROGRESS_KEY));
    } finally {
      db.close();
    }
  }
}

async function openWorkspaceDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') {
    return null;
  }

  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (db.objectStoreNames.contains(STORE_NAME)) {
      db.deleteObjectStore(STORE_NAME);
    }
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME);
    }
  };

  return requestToPromise(request);
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
  });
}

function isWorkspaceSnapshot(value: unknown): value is ReaderWorkspaceSnapshot {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<ReaderWorkspaceSnapshot>;
  return (
    candidate.version === READER_WORKSPACE_SNAPSHOT_VERSION &&
    Number.isInteger(candidate.activeStreamIndex) &&
    Array.isArray(candidate.streams) &&
    candidate.streams.every(isStreamSnapshot)
  );
}

function isStreamSnapshot(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['bookId'] === 'number' &&
    typeof candidate['chapter'] === 'number' &&
    typeof candidate['currentItemIndex'] === 'number' &&
    isStreamSequenceSnapshot(candidate['sequence']) &&
    typeof candidate['scrollTop'] === 'number'
  );
}

function isStreamSequenceSnapshot(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  if (candidate['kind'] === 'canonical') {
    return true;
  }

  return (
    candidate['kind'] === 'fixed-list' &&
    Array.isArray(candidate['items']) &&
    candidate['items'].every((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      const chapter = item as Record<string, unknown>;
      return typeof chapter['bookId'] === 'number' && typeof chapter['chapter'] === 'number';
    })
  );
}

function isHornerProgressState(value: unknown): value is HornerProgressState {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<HornerProgressState>;
  return (
    Number.isInteger(candidate.day) &&
    Number(candidate.day) >= 1 &&
    Array.isArray(candidate.readKeys) &&
    candidate.readKeys.every((key) => typeof key === 'string')
  );
}
